import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearCustomerPortalSession,
  getCustomerPortalSession,
  setCustomerPortalSession,
  subscribeCustomerPortalSession,
} from '../../shared/api/customerPortalSessionStore';
import {
  exchangeCustomerPortalClerkSession,
  fetchCustomerPortalWebSession,
} from '../../shared/api/customerPortalClerkApi';
import {
  buildCustomerPortalCookieSession,
  isAuthenticatedWebSession,
} from '../../shared/api/browserSession';
import { useClerkRuntime } from './ClerkRuntimeProvider';
import {
  CUSTOMER_PORTAL_CLERK_ENABLED,
  ENABLE_AUTH,
  ENABLE_CUSTOMER_PORTAL,
} from '../../shared/utils/constants';

const CustomerPortalSessionContext = createContext({
  session: null,
  isReady: true,
  setSession: () => {},
  clearSession: () => {},
});

function isCustomerPortalPath(pathname = '') {
  return pathname === '/customer-portal' || pathname.startsWith('/customer-portal/');
}

export function useCustomerPortalSession() {
  return useContext(CustomerPortalSessionContext);
}

export default function CustomerPortalSessionProvider({ children }) {
  const location = useLocation();
  const clerkRuntime = useClerkRuntime();
  const [session, setSession] = useState(() => getCustomerPortalSession());
  const [isReady, setIsReady] = useState(() => {
    if (!ENABLE_CUSTOMER_PORTAL || !ENABLE_AUTH) {
      return true;
    }

    const initialSession = getCustomerPortalSession();
    if (initialSession?.accessToken) {
      return true;
    }

    return typeof window !== 'undefined' ? !isCustomerPortalPath(window.location.pathname) : true;
  });
  const bootstrapAttemptRef = useRef('');
  const withinCustomerPortal = isCustomerPortalPath(location.pathname);
  const hasAccessToken = Boolean(session?.accessToken);

  useEffect(() => {
    const unsubscribe = subscribeCustomerPortalSession((nextSession) => {
      setSession(nextSession);
      if (nextSession?.accessToken) {
        setIsReady(true);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (ENABLE_CUSTOMER_PORTAL) {
      return undefined;
    }

    clearCustomerPortalSession();
    return undefined;
  }, []);

  useEffect(() => {
    if (
      CUSTOMER_PORTAL_CLERK_ENABLED &&
      clerkRuntime.isLoaded &&
      !clerkRuntime.isSignedIn &&
      session?.authSource === 'clerk'
    ) {
      clearCustomerPortalSession();
    }
  }, [clerkRuntime.isLoaded, clerkRuntime.isSignedIn, session?.authSource]);

  useEffect(() => {
    if (!ENABLE_CUSTOMER_PORTAL || !ENABLE_AUTH || !withinCustomerPortal) {
      setIsReady(true);
      return undefined;
    }

    if (hasAccessToken) {
      setIsReady(true);
      return undefined;
    }

    const attemptKey = [
      location.pathname,
      String(clerkRuntime.user?.id || ''),
      String(clerkRuntime.isSignedIn),
      String(clerkRuntime.isLoaded),
    ].join(':');
    if (bootstrapAttemptRef.current === attemptKey) {
      return undefined;
    }

    bootstrapAttemptRef.current = attemptKey;
    setIsReady(false);
    let mounted = true;
    const currentSession = getCustomerPortalSession() || {};

    Promise.resolve()
      .then(() => fetchCustomerPortalWebSession())
      .then((result) => {
        if (!mounted) {
          return null;
        }

        if (isAuthenticatedWebSession(result)) {
          setCustomerPortalSession(buildCustomerPortalCookieSession(result, currentSession));
          setIsReady(true);
          return null;
        }

        if (!CUSTOMER_PORTAL_CLERK_ENABLED) {
          clearCustomerPortalSession();
          setIsReady(true);
          return null;
        }

        if (!clerkRuntime.isLoaded) {
          return null;
        }

        if (!clerkRuntime.isSignedIn) {
          clearCustomerPortalSession();
          setIsReady(true);
          return null;
        }

        return clerkRuntime
          .getToken()
          .then((clerkToken) => {
            if (!mounted || !clerkToken) {
              return null;
            }

            return exchangeCustomerPortalClerkSession({ clerkToken });
          })
          .then((exchangeResult) => {
            if (!mounted || !isAuthenticatedWebSession(exchangeResult)) {
              clearCustomerPortalSession();
              setIsReady(true);
              return;
            }

            setCustomerPortalSession(
              buildCustomerPortalCookieSession(exchangeResult, currentSession, 'clerk')
            );
            setIsReady(true);
          });
      })
      .catch(() => {
        if (mounted) {
          clearCustomerPortalSession();
          setIsReady(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [
    clerkRuntime,
    hasAccessToken,
    location.pathname,
    withinCustomerPortal,
  ]);

  const value = useMemo(
    () => ({
      session,
      isReady,
      setSession: (nextSession) => setCustomerPortalSession(nextSession),
      clearSession: () => clearCustomerPortalSession(),
    }),
    [isReady, session]
  );

  return (
    <CustomerPortalSessionContext.Provider value={value}>
      {children}
    </CustomerPortalSessionContext.Provider>
  );
}
